import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, X, Truck, Search } from 'lucide-react';

/**
 * Selecteur de vehicule avec stockage localStorage par chauffeur.
 * Permet d'ajouter/supprimer des vehicules locaux et de les selectionner par autocomplete.
 *
 * Structure stockee :
 *   [{ id, unit_number, plate_number, trailer_number?, trailer_plate?, brand?, model?, year?, vin?, vehicle_type? }]
 */
const STORAGE_KEY_PREFIX = 'propilote_driver_vehicles_';

export const getLocalVehicles = (driverId) => {
  if (!driverId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + driverId);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const saveLocalVehicles = (driverId, list) => {
  if (!driverId) return;
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + driverId, JSON.stringify(list));
  } catch (e) {
    console.error('Failed to save local vehicles', e);
  }
};

const makeId = () => `veh_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const LocalVehicleSelector = ({ driverId, selected, onChange, disabled = false }) => {
  const [vehicles, setVehicles] = useState(() => getLocalVehicles(driverId));
  const [open, setOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [search, setSearch] = useState('');
  const [newVehicle, setNewVehicle] = useState({
    unit_number: '', plate_number: '', trailer_number: '', trailer_plate: '',
    brand: '', model: '', year: '', vin: '',
  });
  const wrapperRef = useRef(null);

  // Reload from localStorage when driverId changes
  useEffect(() => {
    setVehicles(getLocalVehicles(driverId));
  }, [driverId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setShowAddForm(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const persist = (list) => {
    setVehicles(list);
    saveLocalVehicles(driverId, list);
  };

  const filteredVehicles = vehicles.filter((v) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (v.unit_number || '').toLowerCase().startsWith(s) ||
      (v.unit_number || '').toLowerCase().includes(s) ||
      (v.plate_number || '').toLowerCase().includes(s) ||
      (v.trailer_number || '').toLowerCase().includes(s)
    );
  });

  const handleSelect = (vehicle) => {
    onChange(vehicle);
    setOpen(false);
    setSearch('');
    setShowAddForm(false);
  };

  const handleAdd = () => {
    if (!newVehicle.unit_number.trim()) return;
    const created = { id: makeId(), ...newVehicle };
    const next = [...vehicles, created];
    persist(next);
    handleSelect(created);
    setNewVehicle({ unit_number: '', plate_number: '', trailer_number: '', trailer_plate: '', brand: '', model: '', year: '', vin: '' });
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer ce vehicule de la liste locale ?')) return;
    persist(vehicles.filter((v) => v.id !== id));
    if (selected?.id === id) onChange(null);
  };

  const btnLabel = selected ? (
    <span className="flex items-center gap-2 min-w-0">
      <Truck size={16} className="text-amber-500 shrink-0" />
      <span className="text-white font-semibold truncate">#{selected.unit_number}</span>
      {selected.plate_number && <span className="text-slate-400 text-xs truncate">{selected.plate_number}</span>}
    </span>
  ) : (
    <span className="text-slate-400 flex items-center gap-2">
      <Truck size={16} />
      Selectionner un vehicule
    </span>
  );

  return (
    <div className="relative" ref={wrapperRef} data-testid="local-vehicle-selector">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-700'}`}
        data-testid="vehicle-selector-btn"
      >
        {btnLabel}
        <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-30 overflow-hidden">
          {/* Search bar */}
          <div className="p-2 border-b border-slate-800">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Taper debut de # unite..."
                autoFocus
                className="w-full bg-slate-800 border border-slate-700 rounded pl-8 pr-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500"
                data-testid="vehicle-selector-search"
              />
            </div>
          </div>

          {/* Vehicle list */}
          <div className="max-h-64 overflow-y-auto">
            {filteredVehicles.length === 0 && !showAddForm && (
              <div className="p-4 text-center text-slate-500 text-xs">
                {vehicles.length === 0 ? 'Aucun vehicule enregistre. Ajoutez-en un ci-dessous.' : 'Aucun resultat'}
              </div>
            )}
            {filteredVehicles.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => handleSelect(v)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-800 transition-colors ${selected?.id === v.id ? 'bg-amber-500/10 border-l-2 border-amber-500' : ''}`}
                data-testid={`vehicle-option-${v.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Truck size={14} className="text-amber-500 shrink-0" />
                    <span className="text-white font-semibold text-sm">#{v.unit_number}</span>
                    {v.plate_number && <span className="text-slate-400 text-xs">{v.plate_number}</span>}
                  </div>
                  {(v.brand || v.model || v.trailer_number) && (
                    <div className="text-[11px] text-slate-500 mt-0.5 ml-6 truncate">
                      {[v.brand, v.model, v.trailer_number && `Rem. #${v.trailer_number}`].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => handleDelete(v.id, e)}
                  className="text-slate-600 hover:text-red-400 p-1 rounded shrink-0"
                  title="Supprimer"
                  data-testid={`vehicle-delete-${v.id}`}
                >
                  <X size={12} />
                </button>
              </button>
            ))}
          </div>

          {/* Add form */}
          <div className="border-t border-slate-800 bg-slate-950">
            {!showAddForm ? (
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-amber-400 hover:bg-slate-900 text-sm font-medium transition-colors"
                data-testid="vehicle-add-btn"
              >
                <Plus size={14} />
                Ajouter un vehicule
              </button>
            ) : (
              <div className="p-3 space-y-2" data-testid="vehicle-add-form">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    placeholder="# Unite *"
                    value={newVehicle.unit_number}
                    onChange={(e) => setNewVehicle({ ...newVehicle, unit_number: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                    data-testid="new-vehicle-unit"
                  />
                  <input
                    placeholder="Plaque"
                    value={newVehicle.plate_number}
                    onChange={(e) => setNewVehicle({ ...newVehicle, plate_number: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                  />
                  <input
                    placeholder="Marque"
                    value={newVehicle.brand}
                    onChange={(e) => setNewVehicle({ ...newVehicle, brand: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                  />
                  <input
                    placeholder="Modele"
                    value={newVehicle.model}
                    onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                  />
                  <input
                    placeholder="# Remorque"
                    value={newVehicle.trailer_number}
                    onChange={(e) => setNewVehicle({ ...newVehicle, trailer_number: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                  />
                  <input
                    placeholder="Plaque rem."
                    value={newVehicle.trailer_plate}
                    onChange={(e) => setNewVehicle({ ...newVehicle, trailer_plate: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); setNewVehicle({ unit_number: '', plate_number: '', trailer_number: '', trailer_plate: '', brand: '', model: '', year: '', vin: '' }); }}
                    className="flex-1 bg-slate-800 hover:bg-slate-700 text-white text-xs py-1.5 rounded transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleAdd}
                    disabled={!newVehicle.unit_number.trim()}
                    className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold text-xs py-1.5 rounded transition-colors"
                    data-testid="vehicle-add-save"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalVehicleSelector;
